import React, { FC } from 'react';
import { JsonLdContent, Review } from '@navi-toolbox/seo';
import { connect } from 'react-redux';
import { RootReducer } from 'store/root-reducer';
import {
  getDisplayedName,
  getPrimaryPrice,
  getProductId,
  getProductImages,
  getProductMarketingDescription,
  getCharacteristics,
  isWebSellable,
} from 'features/product-info';
import { getBaseUrl } from 'common/selectors';
import { HelmetContext } from '@okapi/helmet-react';
import { getReviewsCards } from 'features/reviews/selectors';
import { getBreadcrumbs } from 'features/page-model';
import { getBrandCharacteristic } from 'common/components/json-ld/utils';
import { getCurrencyByLang, getLang } from 'utils';
import { isWebAvailable as getIsWebAvailable } from 'features/goods-supply';
import { getSeoProperties } from 'features/page-model/ducks/get-model';
import { PdpJsonLdData } from './types';

const DATA_QA_PDP_JSON_LD = 'pdp-json-ld';
const DATA_QA_PDP_JSON_LD_BREADCRUMBS = 'pdp-json-ld-breadcrumbs';

type Props = {
  context: HelmetContext;
};

const PdpJsonLd: FC<PdpJsonLdData> = ({ data, context, breadcrumbs }) => (
  <>
    <JsonLdContent content={breadcrumbs} dataQa={DATA_QA_PDP_JSON_LD_BREADCRUMBS} context={context} />
    <JsonLdContent content={data} dataQa={DATA_QA_PDP_JSON_LD} context={context} />
  </>
);

export const mapStoreToProps = (store: RootReducer, props: Props): PdpJsonLdData => {
  const images = getProductImages(store) || [];
  const { metadescription } = getSeoProperties(store) || {};
  const description = getProductMarketingDescription(store) || metadescription || '';
  const productId = getProductId(store);
  const price = getPrimaryPrice(store);
  const url = getBaseUrl(store);
  const characteristics = getCharacteristics(store);
  const reviewCards = getReviewsCards(store);
  const brgs = getBreadcrumbs(store) || [];
  const isWebAvailable = getIsWebAvailable(store);
  const isSellable = isWebSellable(store);
  const isAvailable = isWebAvailable && isSellable;
  const lang = getLang();
  const defaultPrice = isAvailable ? '' : '0';
  const defaultCurrency = isAvailable ? '' : getCurrencyByLang(lang);
  const breadcrumbs = {
    breadcrumbs: brgs.slice(2),
  };
  const category = brgs.reduce((prev, curr, currentIndex) => {
    if (currentIndex > 1) {
      return !prev ? curr.label : `${prev}>${curr.label}`;
    }
    return prev;
  }, '');

  const priceCurrency = price.currency || defaultCurrency;
  return {
    breadcrumbs,
    data: {
      name: getDisplayedName(store) || '',
      image: images.map((img) => img?.url || ''),
      characteristics,
      description,
      sku: productId,
      brand: getBrandCharacteristic(characteristics),
      category,
      offers: {
        url,
        // FIXME: поправить тип в JsonLdContentProps
        // @ts-expect-error @navi-toolbox/seo/types/json-ld-content/types
        price: Number(price?.price?.toFixed(2) || defaultPrice),
        priceCurrency,
        isAvailable,
        prevPrice: {
          // FIXME: поправить тип в JsonLdContentProps
          // @ts-expect-error @navi-toolbox/seo/types/json-ld-content/types
          price: Number(price?.prevPrice?.toFixed(2) || defaultPrice),
          priceCurrency,
        },
      },
      aggregateRating: {
        ratingValue: '5',
        reviewCount: '100',
      },
      review: reviewCards.map<Review>((card) => ({
        reviewBody: card.body,
        reviewRating: {
          ratingValue: card.ratingScore.toString(),
          bestRating: '5',
          worstRating: '1',
        },
        datePublished: card.createdAt || '',
        authorName: card.author.name,
        pros: card.pros,
        cons: card.cons,
      })),
    },
    context: props.context,
  };
};

export const PdpJsonLdConnected = connect(mapStoreToProps)(PdpJsonLd);
